import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Upload, 
  FileText, 
  CheckCircle, 
  AlertTriangle,
  Play,
  FileSpreadsheet
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FileUpload = () => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [processingFiles, setProcessingFiles] = useState(new Set());

  const onDrop = useCallback(async (acceptedFiles) => {
    setUploading(true);
    
    for (const file of acceptedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API}/files/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        setUploadedFiles(prev => [...prev, {
          ...response.data,
          file: file,
          status: 'uploaded',
          anomalyResults: []
        }]);

        toast.success(`${file.name} uploaded successfully!`);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}: ${error.response?.data?.detail || 'Unknown error'}`);
      }
    }
    
    setUploading(false);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: true
  });

  const runAnomalyDetection = async (fileId, anomalyType) => {
    setProcessingFiles(prev => new Set(prev).add(`${fileId}-${anomalyType}`));
    
    try {
      const response = await axios.post(`${API}/ml/detect-anomalies/${fileId}?anomaly_type=${anomalyType}`);
      
      setUploadedFiles(prev => prev.map(file => {
        if (file.id === fileId) {
          return {
            ...file,
            anomalyResults: [...(file.anomalyResults || []), response.data]
          };
        }
        return file;
      }));

      toast.success(`Anomaly detection completed for ${anomalyType.replace('_', ' ')}`);
    } catch (error) {
      console.error('Anomaly detection error:', error);
      toast.error(`Failed to run anomaly detection: ${error.response?.data?.detail || 'Unknown error'}`);
    } finally {
      setProcessingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(`${fileId}-${anomalyType}`);
        return newSet;
      });
    }
  };

  const getFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const anomalyTypes = [
    { 
      key: 'trial_balance', 
      label: 'Trial Balance Golden Rule Check', 
      description: 'Verifies debit/credit balance correctness according to accounting principles',
      details: 'Validates total debits = total credits, account type consistency, and negative amount detection'
    },
    { 
      key: 'opening_closing_balance', 
      label: 'Opening & Closing Balance Check', 
      description: 'Ensures consistency across accounting periods',
      details: 'Checks balance calculations, excessive changes, and asset account validation'
    },
    { 
      key: 'purchase_reconciliation', 
      label: 'Purchase Register Reconciliation (GSTR-2B)', 
      description: 'Matches purchase records with GST filings',
      details: 'GSTIN validation, invoice verification, tax calculation, and GSTR-2B compliance'
    },
    { 
      key: 'sales_reconciliation', 
      label: 'Sales Register Reconciliation (GSTR-2B)', 
      description: 'Validates sales entries against GST data',
      details: 'Customer GSTIN checks, B2B/B2C validation, export transaction rules, and GST compliance'
    }
  ];

  return (
    <div className="space-y-6" data-testid="file-upload-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">File Upload & Analysis</h1>
          <p className="text-gray-600 mt-2">Upload financial ledgers and run anomaly detection</p>
        </div>
      </div>

      {/* Upload Zone */}
      <Card className="border-dashed border-2 border-gray-300 hover:border-blue-400 transition-colors">
        <CardContent className="p-0">
          <div
            {...getRootProps()}
            className={`cursor-pointer p-12 text-center transition-colors ${
              isDragActive ? 'bg-blue-50 border-blue-400' : 'hover:bg-gray-50'
            }`}
            data-testid="upload-dropzone"
          >
            <input {...getInputProps()} />
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="bg-blue-100 p-4 rounded-full">
                  <Upload className="h-8 w-8 text-blue-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {isDragActive ? 'Drop files here...' : 'Drop files or click to browse'}
                </h3>
                <p className="text-gray-600">
                  Supports Excel files (.xlsx, .xls) up to 10MB
                </p>
              </div>
              {uploading && (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm text-gray-600">Uploading...</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">Uploaded Files</h2>
          
          {uploadedFiles.map((file) => (
            <Card key={file.id} className="shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <FileSpreadsheet className="h-6 w-6 text-green-600" />
                    <div>
                      <CardTitle className="text-lg">{file.original_filename}</CardTitle>
                      <CardDescription>
                        Size: {getFileSize(file.file_size)} • Uploaded: {new Date(file.upload_date).toLocaleString()}
                      </CardDescription>
                    </div>
                  </div>
                  <Badge variant="success" className="bg-green-100 text-green-800">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Uploaded
                  </Badge>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Anomaly Detection Controls */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Run Anomaly Detection:</h4>
                  <div className="grid gap-2 md:grid-cols-2">
                    {anomalyTypes.map((type) => {
                      const isProcessing = processingFiles.has(`${file.id}-${type.key}`);
                      const hasResults = file.anomalyResults?.some(result => result.anomaly_type === type.key);
                      
                      return (
                        <Button
                          key={type.key}
                          variant={hasResults ? "success" : "outline"}
                          size="sm"
                          onClick={() => runAnomalyDetection(file.id, type.key)}
                          disabled={isProcessing}
                          className="justify-start h-auto p-3"
                          data-testid={`run-detection-${type.key}`}
                        >
                          <div className="flex items-center w-full">
                            {isProcessing ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                            ) : hasResults ? (
                              <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                            ) : (
                              <Play className="h-4 w-4 mr-2" />
                            )}
                            <div className="text-left flex-1">
                              <div className="font-medium text-sm">{type.label}</div>
                              <div className="text-xs opacity-75 mb-1">{type.description}</div>
                              <div className="text-xs opacity-60 text-gray-600">{type.details}</div>
                            </div>
                          </div>
                        </Button>
                      );
                    })}
                  </div>
                </div>

                {/* Results Summary */}
                {file.anomalyResults && file.anomalyResults.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900">Detection Results:</h4>
                    {file.anomalyResults.map((result) => (
                      <Alert key={result.id} className="border-l-4 border-l-blue-500">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium capitalize">
                              {result.anomaly_type.replace('_', ' ')}
                            </span>
                            <Badge variant="outline">
                              Confidence: {Math.round(result.confidence_score * 100)}%
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{result.summary}</p>
                          
                          {result.detected_anomalies.length > 0 && (
                            <div className="space-y-1">
                              <p className="text-sm font-medium">Detected Issues:</p>
                              {result.detected_anomalies.slice(0, 3).map((anomaly, idx) => (
                                <div key={idx} className={`text-xs px-2 py-1 rounded border ${getSeverityColor(anomaly.severity)}`}>
                                  <span className="font-medium">{anomaly.type}:</span> {anomaly.description}
                                  {anomaly.row && <span className="ml-2">(Row {anomaly.row})</span>}
                                </div>
                              ))}
                              {result.detected_anomalies.length > 3 && (
                                <p className="text-xs text-gray-500">
                                  +{result.detected_anomalies.length - 3} more issues...
                                </p>
                              )}
                            </div>
                          )}
                        </AlertDescription>
                      </Alert>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Help Section */}
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center text-blue-900">
            <FileText className="h-5 w-5 mr-2" />
            Supported File Formats & Requirements
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-blue-800 space-y-2">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h4 className="font-medium mb-2">File Requirements:</h4>
              <ul className="space-y-1 text-sm">
                <li>• Excel files (.xlsx, .xls)</li>
                <li>• Maximum size: 10MB</li>
                <li>• Structured financial data</li>
                <li>• Clear column headers</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">Financial Detection Modules:</h4>
              <ul className="space-y-1 text-sm">
                <li>• <strong>Trial Balance Golden Rule:</strong> Debit/Credit validation</li>
                <li>• <strong>Balance Consistency:</strong> Opening/Closing balance checks</li>
                <li>• <strong>Purchase GSTR-2B:</strong> GST purchase reconciliation</li>
                <li>• <strong>Sales GSTR-2B:</strong> GST sales validation</li>
                <li>• <strong>Account Type Validation:</strong> Asset/Liability/Revenue rules</li>
                <li>• <strong>GSTIN Compliance:</strong> 15-digit format validation</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FileUpload;